import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmployeeDefaultValues } from "@shared/schema";

type OwnerType = "agency" | "client" | "prospect";

interface EmployeeDefaultSettingsResponse {
  ownerType: OwnerType;
  ownerId: string;
  defaults: Partial<EmployeeDefaultValues>;
  updatedAt: string | null;
}

interface UseEmployeeDefaultSettingsResult {
  isLoading: boolean;
  isUpdating: boolean;
  defaults: Partial<EmployeeDefaultValues>;
  updatedAt: string | null;
  refresh: () => void;
  updateDefaults: (next: Partial<EmployeeDefaultValues>) => Promise<void>;
}

const resolveEndpoint = (ownerType: OwnerType, ownerId?: string) => {
  if (ownerType === "agency") {
    return "/api/agency/employee-defaults";
  }

  if (!ownerId) {
    throw new Error("ownerId is required for client and prospect defaults");
  }

  const base =
    ownerType === "client"
      ? `/api/clients/${ownerId}/employee-defaults`
      : `/api/prospects/${ownerId}/employee-defaults`;
  return base;
};

export function useEmployeeDefaultSettings(
  ownerType: OwnerType,
  ownerId?: string
): UseEmployeeDefaultSettingsResult {
  const queryClient = useQueryClient();
  const endpoint = useMemo(
    () => resolveEndpoint(ownerType, ownerId),
    [ownerType, ownerId]
  );
  const queryKey = useMemo(
    () => ["employee-default-settings", ownerType, ownerId ?? "self"],
    [ownerType, ownerId]
  );

  const {
    data,
    isLoading,
    refetch: refetchDefaults,
  } = useQuery<EmployeeDefaultSettingsResponse>({
    queryKey,
    queryFn: async () => {
      const response = await fetch(endpoint, { credentials: "include" });
      if (!response.ok) {
        throw new Error("Failed to load employee defaults");
      }
      return response.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (next: Partial<EmployeeDefaultValues>) => {
      const response = await fetch(endpoint, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ defaults: next }),
      });
      if (!response.ok) {
        throw new Error("Failed to update employee defaults");
      }
      return response.json() as Promise<EmployeeDefaultSettingsResponse>;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
    },
  });

  const defaults = data?.defaults ?? {};

  return {
    isLoading,
    isUpdating: mutation.isPending,
    defaults,
    updatedAt: data?.updatedAt ?? null,
    refresh: () => {
      void refetchDefaults();
    },
    updateDefaults: async (next: Partial<EmployeeDefaultValues>) => {
      await mutation.mutateAsync(next);
    },
  };
}
